import React, { useState, useEffect } from 'react';
import { Grid, makeStyles, Dialog, DialogTitle, DialogContent, Chip } from '@material-ui/core';
import { useDropzone } from 'react-dropzone';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Button, TextField, Typography } from '@swagup-com/components';
import isEmpty from 'lodash/isEmpty';
import CustomizedSelect from './CustomizedSelect';
import { decorationName, hasExtension } from './common';
import styles, { textFieldStyles, modalStyles } from './styles/itemsDecorationPanel';
import { truncateFileName } from '../../../helpers/utils';
import LimitedTextField from '../../global/LimitedTextField';

const useStyles = makeStyles(styles);
const useTextFieldStyles = makeStyles(textFieldStyles);
const useModalStyles = makeStyles(modalStyles);

const hasArtwork = decoration => decoration.Artwork1 && decoration.Artwork1_S3_Location;

const DecorationFieldEdit = ({
  onChange,
  decoration,
  name,
  placeholder,
  label,
  tip,
  multiline,
  editable,
  disabled,
  compactStyle,
  maxLength = 250
}) => {
  const classes = useTextFieldStyles({ compactStyle: !!compactStyle, disabled });

  const textComponent = multiline ? (
    <LimitedTextField
      value={decoration?.[name] || ''}
      name={name}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={500}
      onChange={onChange}
      className={classes.longInputText}
    />
  ) : (
    <TextField
      className={classes.inputText}
      placeholder={placeholder}
      disabled={disabled}
      value={decoration?.[name] || ''}
      name={name}
      onChange={onChange}
      inputProps={{ maxLength }}
    />
  );

  return (
    <div className={classes.decorationEditSection}>
      <p className={classes.label}>
        {label || name}
        {tip && <span className={classes.tip}>{tip}</span>}
      </p>
      {editable ? textComponent : <p className={classes.valueText}>{decoration?.[name] || 'To be set...'}</p>}
    </div>
  );
};

const ActionSection = ({ children, onAccept, onDecline, acceptText, declineText, disabled, showDeclineBtn = true }) => {
  const classes = useStyles({
    color: declineText === 'Delete Decoration' ? '#F44336' : '#3577d4',
    backgroundColor: acceptText === 'Delete' ? '#F44336' : '#3577d4'
  });

  const variant = acceptText === 'Add Request' || acceptText === 'Update Request' ? 'secondary' : 'primary';
  const className =
    acceptText === 'Delete'
      ? classes.wizardButtons
      : (variant === 'secondary' && classes.addRequestButton) || classes.declineButton;

  return (
    <Grid container justifyContent={variant === 'secondary' ? 'space-between' : 'flex-end'} alignItems="center">
      <Grid item>
        {showDeclineBtn && (
          <Button variant="text" size="small" onClick={onDecline} disabled={disabled} className={classes.declineButton}>
            {declineText}
          </Button>
        )}
      </Grid>
      <Grid item>
        {children || (
          <Button variant={variant} size="small" onClick={onAccept} className={className} disabled={disabled}>
            {acceptText}
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

const FileUploadZone = ({ handleFileUpload, children, disabled, renderComponent }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
    accept: 'image/*, application/pdf, application/illustrator, application/postscript'
  });
  return (
    <Grid container {...getRootProps()} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
      <input {...getInputProps()} disabled={disabled} />
      {renderComponent ? renderComponent({ isDragActive }) : children}
    </Grid>
  );
};

const ArtworkDisplay = ({ decoration, onDeleteImage }) => {
  const classes = useStyles();
  return (
    <Grid container direction="column" alignItems="left">
      <Grid item>
        <Grid container alignItems="center" style={{ marginTop: 12, marginBottom: 12 }}>
          <Grid item className={classes.chipContainer}>
            <Chip
              className={classes.chipBox}
              label={truncateFileName(decoration.Artwork1, 22)}
              onDelete={onDeleteImage}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const DecorationActionsModal = ({ isDeletingModal, open, title, subtitle, onDecline, handleFileUpload, onAccept }) => {
  const classes = useModalStyles();

  return (
    <Dialog classes={classes} open={open} onClose={onDecline}>
      <DialogTitle>
        <Typography variant="body2SemiBoldInter" style={{ marginBottom: 16 }}>
          {title}
        </Typography>
        <Typography variant="body3RegularInter">{subtitle}</Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container justifyContent="flex-end" alignItems="center" className={classes.buttonsContainer}>
          {isDeletingModal ? (
            <ActionSection onAccept={onAccept} onDecline={onDecline} declineText="Cancel" acceptText="Delete" />
          ) : (
            <ActionSection onDecline={onDecline} declineText="Use Current File">
              <FileUploadZone handleFileUpload={handleFileUpload}>
                <Button variant="primary" size="small" className={classes.wizardButtons}>
                  Upload Vector
                </Button>
              </FileUploadZone>
            </ActionSection>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

const ItemsDecorationPanel = ({ item, decoration, onChange, onDelete, disabled, changes, isNewForm }) => {
  const [uploadWarning, setUploadWarning] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState(false);
  const [localDecoration, setLocalDecoration] = useState(decoration);

  useEffect(() => {
    setLocalDecoration(decoration);
  }, [decoration]);

  const classes = useStyles({ disabled, uploadWarning, deleteWarning });

  const handleFileUpload = acceptedFiles => {
    const image = acceptedFiles[0];
    const filePath = URL.createObjectURL(image);

    if (image) {
      const name = image.name.replace(/\s+/g, '-');
      setLocalDecoration(prev => ({
        ...prev,
        artwork: { image, fileName: name },
        Artwork1: name,
        Artwork1_S3_Location: filePath
      }));
    }

    setUploadWarning(!hasExtension(image.name, ['.svg', '.eps', '.ai']));
  };

  const handleTextChange = ({ target: { name, value } }) => setLocalDecoration(prev => ({ ...prev, [name]: value }));

  const handleDeleteImage = () =>
    setLocalDecoration(prev => {
      const { artwork, ...prevDecor } = prev;
      return { ...prevDecor, Artwork1: '', Artwork1_S3_Location: '' };
    });

  const toggleExpanded = () => setLocalDecoration(prev => ({ ...prev, expanded: !prev.expanded }));

  const handleAddDecoration = () =>
    onChange({
      ...localDecoration,
      expanded: false,
      Name: isNewForm
        ? `New Decoration Request #${(changes.decorations?.filter(d => d.created).length || 0) + 1}`
        : localDecoration.Name
    });

  const fileUploadSrc = `/images/public/file-upload${disabled ? '-disabled' : ''}.svg`;

  return (
    <div className={classes.panelBody}>
      <Accordion className={classes.mainAccordian} expanded={localDecoration.expanded} onChange={toggleExpanded}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className={classes.closebtn} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          className={classes.summaryAccordian}
        >
          <p className={classes.header}>{isNewForm ? 'New Decoration Request' : decorationName(localDecoration)}</p>
        </AccordionSummary>

        <AccordionDetails>
          <>
            <Grid container alignItems="center" style={{ height: '100%' }}>
              <p className={classes.logoName} style={{ marginBottom: 8 }}>
                Artwork for {item ? item.name : 'Product'}
              </p>
              {hasArtwork(localDecoration) && (
                <>
                  <Grid container alignItems="center">
                    <Grid item xs>
                      <ArtworkDisplay decoration={localDecoration} onDeleteImage={handleDeleteImage} />
                    </Grid>
                  </Grid>
                </>
              )}
              <Grid container>
                <FileUploadZone
                  handleFileUpload={handleFileUpload}
                  disabled={disabled}
                  renderComponent={props => (
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      className={classes.ImageUploadContainer}
                      style={{ border: props?.isDragActive ? '1px dotted #3577d4' : undefined }}
                    >
                      <Grid item className={classes.imgContainer}>
                        <Grid container justifyContent="center" className={classes.iconWrapper}>
                          <img src={fileUploadSrc} alt="upload" />
                        </Grid>
                        <Grid>
                          <p className={classes.uploadText}>
                            Drag & Drop to upload or{' '}
                            <span className={classes.link} style={{ color: disabled ? '#989EA4' : undefined }}>
                              browse
                            </span>
                          </p>
                          <p className={classes.fileFormats}>Accepted file types: SVG, EPS, or AI</p>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
              {!isEmpty(item.available_imprint_options) && (
                <div className={classes.decorationEditSection}>
                  <p className={classes.imprintType}>Imprint Type</p>
                  <CustomizedSelect
                    value={localDecoration.Imprint_Type}
                    items={item.available_imprint_options}
                    onChange={value => setLocalDecoration(prev => ({ ...prev, Imprint_Type: value }))}
                    disabled={disabled}
                  />
                </div>
              )}
              {!isEmpty(localDecoration?.Colors) && (
                <div className={classes.decorationEditSection}>
                  <p className={classes.imprintType}>Decoration Color(s)</p>
                  <div className={classes.colorList}>
                    {localDecoration?.Colors?.map(color => (
                      <p key={color}>{color}</p>
                    ))}
                  </div>
                </div>
              )}
              <DecorationFieldEdit
                decoration={localDecoration}
                name="Location"
                placeholder="Specify the placement of your artwork"
                onChange={handleTextChange}
                editable={localDecoration.created}
                disabled={disabled}
              />
              <DecorationFieldEdit
                decoration={localDecoration}
                name="Dimensions (Optional)"
                placeholder="Don’t worry if you don’t know exact dimensions"
                onChange={handleTextChange}
                editable={localDecoration.created}
                disabled={disabled}
              />
              <div className={classes.notesWrapper}>
                <DecorationFieldEdit
                  multiline
                  decoration={localDecoration}
                  name="Notes"
                  placeholder="Type here if you have any additional request"
                  label="Additional Design Notes"
                  onChange={handleTextChange}
                  editable
                  compactStyle
                  maxLength={500}
                  disabled={disabled}
                />
              </div>
              <ActionSection
                classes={classes}
                onAccept={handleAddDecoration}
                onDecline={() => setDeleteWarning(true)}
                declineText="Delete Decoration"
                acceptText={isNewForm ? 'Add Request' : 'Update Request'}
                disabled={disabled}
                showDeclineBtn={!!localDecoration.created && !isNewForm}
              />
            </Grid>
          </>
        </AccordionDetails>
      </Accordion>

      <DecorationActionsModal
        open={uploadWarning}
        title="Non-vector art file"
        subtitle="Please upload a vector-file (SVG, EPS, or AI) instead. If you don’t have one, our designers can convert your current file (results may vary)."
        onDecline={() => setUploadWarning(false)}
        handleFileUpload={handleFileUpload}
      />
      <DecorationActionsModal
        isDeletingModal
        open={deleteWarning}
        title={`Are you sure you want to delete ${decorationName(localDecoration)}?`}
        subtitle="Deleting this decoration will send our designers a notification to review this item and create a replacement design if necessary."
        onDecline={() => setDeleteWarning(false)}
        onAccept={() => onDelete(localDecoration.id)}
      />
    </div>
  );
};

export default ItemsDecorationPanel;
